/**
 * Where an automatically split deposit went, in words (2026-11-01).
 *
 *   Added by Parent                          +$10.00
 *   Split: Added to Bike $2.00. Added to Ravi $8.00.
 *
 * A family's automatic splits set part of every deposit aside in goals, so
 * "+$10" on its own told a child they had ten more to spend when they had
 * eight. This line says where each part went, the same on the parent's
 * screens and on the child's own device.
 *
 * The server records the goal parts (`autoSaved` on the entry, `saved` on the
 * child's own row). What the child kept is the rest of their share: a
 * subtraction for READING, like every sum in this app's display code. Nothing
 * is sent back; the ledger is still summed in domain.py.
 */

import { dollars } from './money';

/** One child's part: their share of the entry and what was set aside from it. */
export interface SplitPart {
  name: string;
  share: number;
  saved: Record<string, number> | undefined;
}

function pieces(part: SplitPart, goalTitle: (goalId: string) => string): string[] {
  const into = Object.entries(part.saved ?? {}).filter(([, cents]) => cents > 0);
  if (!into.length || part.share <= 0) return [];
  const out = into.map(([goalId, cents]) => `Added to ${goalTitle(goalId) || 'a goal'} ${dollars(cents)}`);
  const kept = part.share - into.reduce((sum, [, cents]) => sum + cents, 0);
  if (kept > 0) out.push(`Added to ${part.name || 'them'} ${dollars(kept)}`);
  return out;
}

/**
 * "Split: Added to Bike $2.00. Added to Ravi $8.00." -- or null when nothing
 * was split. Several children on one entry read one after another, each
 * child's goals before what they kept.
 */
export function splitLine(parts: SplitPart[], goalTitle: (goalId: string) => string): string | null {
  const all = parts.flatMap((part) => pieces(part, goalTitle));
  return all.length ? `Split: ${all.join('. ')}.` : null;
}

/** The parts of a family entry, from its shares and `autoSaved`. */
export function entryParts(
  entry: { shares: Record<string, number>; autoSaved?: Record<string, Record<string, number>> },
  nameOf: (personId: string) => string,
): SplitPart[] {
  return Object.entries(entry.autoSaved ?? {}).map(([pid, saved]) => ({
    name: nameOf(pid),
    share: entry.shares[pid] ?? 0,
    saved,
  }));
}
